import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, DialogContent, Typography } from '@mui/material';
import PlatformDialog from '../../molecules/PlatformDialog/PlatformDialog';
import ArticleModalView from '../Dialogs/ArticleModal/ArticleModalView';
import {
  useArticleModalFullscreen,
  useArticleModalSwipeNavigation
} from '../Dialogs/ArticleModal/articleModalHooks';
import useContentSearch from '../../../hooks/useContentSearch';
import useTextToSpeech from '../../../hooks/useTextToSpeech';
import api from '../../../services/api';
import { getYouTubeId } from '../../../utils/contentProcessor';
import { isQualitativeAnalysisAiApplied } from '../../../utils/aiAnalysisStatus';
import {
  buildSemanticHighlights,
  getReducedFontSize
} from '../Dialogs/ArticleModal/articleModalUtils';
import { buildArticleTTSContent } from '../Dialogs/ArticleModal/articleModalContentUtils';

const noop = () => {};

const PublicArticleModal = ({
  articleId,
  open,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext
}) => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shareMenuAnchor, setShareMenuAnchor] = useState(null);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [kpiaOpen, setKpiaOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const contentContainerRef = useRef(null);
  const videoRef = useRef(null);

  const {
    closeFullscreen,
    fullscreenOpen,
    fullscreenZoom,
    openFullscreen,
    resetZoom,
    zoomIn,
    zoomOut
  } = useArticleModalFullscreen();
  const { handleSwipeEnd, handleSwipeStart } = useArticleModalSwipeNavigation({ onNext, onPrev });
  const {
    closeSearch,
    currentMatch,
    goToNextMatch,
    goToPrevMatch,
    isSearchOpen,
    openSearch,
    searchTerm,
    setSearchTerm,
    totalMatches
  } = useContentSearch(contentContainerRef, open);
  const tts = useTextToSpeech();

  useEffect(() => {
    if (!open || !articleId) return undefined;

    let cancelled = false;

    const fetchArticle = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/articles/public/${articleId}`);
        if (!cancelled) {
          setArticle(response.data.data);
        }
      } catch (err) {
        console.error('Erro ao carregar artigo publico:', err);
        if (!cancelled) {
          setArticle(null);
          setError('Artigo nao encontrado');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchArticle();

    return () => {
      cancelled = true;
    };
  }, [articleId, open]);

  useEffect(() => {
    if (!open) {
      tts.stop();
      setShareMenuAnchor(null);
      setActionAnchorEl(null);
      setKpiaOpen(false);
    }
  }, [open, tts]);

  const semanticHighlights = useMemo(() => buildSemanticHighlights(article), [article]);
  const youtubeId = useMemo(() => (
    article?.videoId || article?.youtubeId || getYouTubeId(article?.videoUrl || article?.siteUrl)
  ), [article]);
  const qualitativeAnalysisApplied = isQualitativeAnalysisAiApplied(article);
  const isArticleReady = article?._id === articleId;

  const handleClose = () => {
    tts.stop();
    closeSearch();
    setKpiaOpen(false);
    onClose();
  };

  const handleOpenShare = (event) => {
    setShareMenuAnchor(event.currentTarget);
  };

  const handleCloseShare = () => {
    setShareMenuAnchor(null);
  };

  const handleOpenMore = (event) => {
    setActionAnchorEl(event.currentTarget);
  };

  const handleCloseMore = () => {
    setActionAnchorEl(null);
  };

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleOpenPublicLink = () => {
    if (article?._id) {
      window.open(`/article/${article._id}`, '_blank', 'noopener,noreferrer');
    }
  };

  const getTTSContent = () => buildArticleTTSContent(article);

  if (!open) return null;

  if (loading || error || !isArticleReady) {
    return (
      <PlatformDialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            minHeight: { xs: '100vh', sm: '70vh' },
            borderRadius: { xs: 0, sm: 3 },
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary">{error || 'Artigo nao encontrado'}</Typography>
            </Box>
          )}
        </DialogContent>
      </PlatformDialog>
    );
  }

  return (
    <ArticleModalView
      readOnly
      article={article}
      availableTags={[]}
      auxiliary={{
        collectionDialogOpen: false,
        exposureAnchor: null,
        exposureAnalyzing: false,
        fullscreenOpen,
        fullscreenZoom,
        lastAddedTag: null,
        rootArticleId: article._id,
        sentimentAnchor: null,
        sentimentAnalyzing: false,
        shareMenuAnchor,
        similarDialogOpen: false,
        snackbarMessage,
        snackbarOpen,
        tagDialogOpen: false,
        onAddTags: noop,
        onAnalyzeExposure: noop,
        onAnalyzeSentiment: noop,
        onCloseCollectionDialog: noop,
        onCloseExposure: noop,
        onCloseFullscreen: closeFullscreen,
        onCloseSentiment: noop,
        onCloseShare: handleCloseShare,
        onCloseSimilarDialog: noop,
        onCloseSnackbar: () => setSnackbarOpen(false),
        onCloseTagDialog: noop,
        onCollectionSuccess: noop,
        onCreateNewsletter: noop,
        onNotify: showSnackbar,
        onSelectExposure: noop,
        onSelectSentiment: noop,
        onSimilarSuccess: noop,
        onZoomIn: zoomIn,
        onZoomOut: zoomOut,
        onZoomReset: resetZoom
      }}
      content={{
        loadingFullContent: false,
        loadedContentProps: {
          actionAnchorEl,
          briefingKeywords: { primary: [], competitor: [] },
          contentContainerRef,
          currentMatch,
          fontSize: getReducedFontSize(undefined),
          highlightEnabled: false,
          highlights: [],
          isMobile: false,
          isOpen: open,
          isPaused: tts.isPaused,
          isSearchOpen,
          isSpeaking: tts.isSpeaking,
          isTTSSupported: tts.isSupported,
          progress: tts.progress,
          rate: tts.rate,
          searchTerm,
          semanticHighlights,
          showBriefingHighlights: false,
          totalMatches,
          videoRef,
          youtubeId,
          onAddHighlight: noop,
          onAddTag: noop,
          onAddToCollection: noop,
          onAnalyzeKpia: noop,
          onBack: handleClose,
          onChangeRate: tts.changeRate,
          onClose: handleClose,
          onCloseActionMenu: handleCloseMore,
          onCloseSearch: closeSearch,
          onDelete: null,
          onEdit: null,
          onListen: () => tts.speak(getTTSContent()),
          onNextMatch: goToNextMatch,
          onOpenExposureMenu: noop,
          onOpenFullscreen: openFullscreen,
          onOpenKpia: () => setKpiaOpen(true),
          onOpenPublicLink: handleOpenPublicLink,
          onOpenSearch: openSearch,
          onOpenSentimentMenu: noop,
          onOpenShare: handleOpenShare,
          onOpenTagDialog: noop,
          onPrevMatch: goToPrevMatch,
          onRecalculateKpia: noop,
          onRemoveHighlight: noop,
          onRemoveTag: null,
          onSearchChange: setSearchTerm,
          onStopSpeech: tts.stop,
          onToggleFavorite: noop,
          onToggleHighlight: noop,
          onToggleRead: noop,
          onToggleSpeech: () => tts.toggle(getTTSContent())
        }
      }}
      dialog={{
        onClose: handleClose,
        onTouchEnd: handleSwipeEnd,
        onTouchStart: handleSwipeStart,
        open
      }}
      footer={{
        highlightEnabled: false,
        isPaused: tts.isPaused,
        isSearchOpen,
        isSpeaking: tts.isSpeaking,
        isTTSSupported: tts.isSupported,
        onAddToCollection: noop,
        onAnalyzeKpia: noop,
        onDelete: null,
        onEdit: null,
        onListen: () => tts.speak(getTTSContent()),
        onOpenExposureMenu: noop,
        onOpenMore: handleOpenMore,
        onOpenPublicLink: handleOpenPublicLink,
        onOpenSearch: openSearch,
        onOpenShare: handleOpenShare,
        onOpenTagDialog: noop,
        onOpenSentimentMenu: noop,
        onRemoveTag: null,
        onToggleFavorite: noop,
        onToggleHighlight: noop,
        onToggleRead: noop
      }}
      kpia={{
        kpiaAnalyzing: false,
        onClose: () => setKpiaOpen(false),
        onRecalculate: noop,
        open: kpiaOpen,
        qualitativeAnalysisApplied
      }}
      navigation={{
        leftArrow: hasPrev,
        onNext,
        onPrev,
        open,
        rightArrow: hasNext
      }}
    />
  );
};

PublicArticleModal.propTypes = {
  articleId: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  hasPrev: PropTypes.bool,
  hasNext: PropTypes.bool
};

PublicArticleModal.defaultProps = {
  articleId: null,
  onPrev: noop,
  onNext: noop,
  hasPrev: false,
  hasNext: false
};

export default PublicArticleModal;
